import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Tiny global cache for the "show_margin" preference.
 * The setting belongs to NotificationSettings (per-store) and changes very
 * rarely, so we fetch it once per session and broadcast updates through a
 * window event.
 *
 * Components can also import `setMarginPreferenceCache` to push a new value
 * immediately (e.g. right after the user toggles the switch in Settings) so
 * the UI updates without waiting for the next reload.
 */
let cachedValue = null; // null = not loaded yet
let pendingFetch = null;
const listeners = new Set();

function emit(value) {
  cachedValue = value;
  listeners.forEach((cb) => { try { cb(value); } catch (_e) { /* noop */ } });
}

export function setMarginPreferenceCache(value) {
  emit(!!value);
}

async function loadOnce() {
  if (cachedValue !== null) return cachedValue;
  if (pendingFetch) return pendingFetch;
  pendingFetch = api
    .getNotificationSettings()
    .then((s) => {
      const v = s?.show_margin ?? true;
      emit(v);
      return v;
    })
    .catch(() => {
      // On error fall back to default (show)
      emit(true);
      return true;
    })
    .finally(() => {
      pendingFetch = null;
    });
  return pendingFetch;
}

/**
 * `useMarginPreference()` returns `true` while loading (default visible) and
 * the actual stored value once available. Re-renders when the value changes.
 */
export function useMarginPreference() {
  const [show, setShow] = useState(cachedValue ?? true);
  useEffect(() => {
    let mounted = true;
    const onChange = (v) => { if (mounted) setShow(v); };
    listeners.add(onChange);
    loadOnce().then((v) => mounted && setShow(v));
    return () => {
      mounted = false;
      listeners.delete(onChange);
    };
  }, []);
  return show;
}
